import { useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { useTheme } from '../hooks/useTheme';
import { useProfilePicture } from '../hooks/useProfilePicture';
import { COLORS, FONTS, LAYOUT } from '../styles/tokens';
import { ProfileDropdown } from './ProfileDropdown';
import { ProfileModal } from './ProfileModal';
import { SecurityModal } from './SecurityModal';

interface TopNavProps {
  tabs: string[];
  activeTab: string;
  onTabChange: (tab: string) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
}

/** Top navigation bar with dynamic tabs, search, and profile */
export function TopNav({ tabs, activeTab, onTabChange, searchQuery, onSearchChange }: TopNavProps): React.JSX.Element {
  const { isDark } = useTheme();
  const { username, fullName, roles } = useAuth();
  const { pictureUrl } = useProfilePicture(username);
  const colors = isDark ? COLORS.dark : COLORS.light;
  const isAdmin = roles.includes('admin');
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showSecurityModal, setShowSecurityModal] = useState(false);

  return (
    <>
      <header
        className="vx-top-nav"
        style={{
          height: `${LAYOUT.topNavHeight}px`,
          backgroundColor: colors.card,
          borderBottom: `1px solid ${colors.border}`,
          display: 'flex',
          alignItems: 'center',
          padding: '0 20px',
          gap: '16px',
          transition: 'background-color 0.3s, border-color 0.3s',
          flexShrink: 0,
        }}
      >
        {/* Tab buttons */}
        <nav className="vx-tab-scroller" style={{ display: 'flex', gap: '4px' }}>
          {tabs.map(tab => {
            const isActive = activeTab === tab;
            return (
              <button
                key={tab}
                onClick={() => onTabChange(tab)}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: FONTS.heading,
                  fontSize: '11px',
                  letterSpacing: '0.15em',
                  textTransform: 'uppercase',
                  color: isActive ? '#FFFFFF' : (isDark ? '#D0D0D0' : '#333333'),
                  backgroundColor: isActive
                    ? (isDark ? 'rgba(1, 169, 237, 0.15)' : 'rgba(1, 101, 198, 0.1)')
                    : 'transparent',
                  transition: 'background-color 0.2s, color 0.2s',
                }}
              >
                {tab}
              </button>
            );
          })}
        </nav>

        {/* Spacer */}
        <div className="vx-top-nav-spacer" style={{ flex: 1 }} />

        {/* Search bar */}
        <div
          className={`vx-search${searchExpanded || searchQuery ? ' is-open' : ''}`}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '0 12px',
            height: '36px',
            borderRadius: '8px',
            backgroundColor: isDark ? COLORS.dark.cardAlt : COLORS.light.cardAlt,
            border: `1px solid ${colors.border}`,
            width: searchExpanded ? '240px' : '160px',
            transition: 'width 0.3s ease',
            flexShrink: 0,
          }}
        >
          {/* Search icon */}
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={colors.textSecondary} strokeWidth="2" strokeLinecap="round">
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          <input
            type="text"
            placeholder="Search apps..."
            value={searchQuery}
            onChange={e => onSearchChange(e.target.value)}
            onFocus={() => setSearchExpanded(true)}
            onBlur={() => { if (!searchQuery) setSearchExpanded(false); }}
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              background: 'transparent',
              fontFamily: FONTS.body,
              fontSize: '12px',
              color: colors.text,
            }}
          />
        </div>

        {/* Profile avatar (clickable) */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setShowProfile(prev => !prev)}
            title={`${fullName || username}${isAdmin ? ' (Admin)' : ''}`}
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              backgroundColor: isDark ? COLORS.accent.blueDark : COLORS.accent.blue,
              display: 'grid',
              placeItems: 'center',
              color: '#FFFFFF',
              fontFamily: FONTS.heading,
              fontSize: '12px',
              fontWeight: 600,
              letterSpacing: '0.05em',
              cursor: 'pointer',
              border: isAdmin ? `2px solid ${COLORS.accent.gold}` : 'none',
              padding: 0,
              overflow: 'hidden',
            }}
          >
            {pictureUrl ? (
              <img src={pictureUrl} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              (fullName || username || 'U').charAt(0).toUpperCase()
            )}
          </button>
          {showProfile && (
            <ProfileDropdown
              onClose={() => setShowProfile(false)}
              onOpenProfile={() => setShowProfileModal(true)}
              onOpenSecurity={() => setShowSecurityModal(true)}
              pictureUrl={pictureUrl}
            />
          )}
        </div>
      </header>

      {/* Profile Modal */}
      {showProfileModal && (
        <ProfileModal onClose={() => setShowProfileModal(false)} />
      )}

      {/* Security Modal */}
      {showSecurityModal && (
        <SecurityModal onClose={() => setShowSecurityModal(false)} />
      )}
    </>
  );
}
